import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import type { ComponentLike } from '@glint/template';
import Heading from 'tio-ui/components/heading';

interface ListRowCardSignature {
  Args: {
    /** Route the whole row links to. */
    route: string;
    /** Model for `@route`, usually the record id. */
    model: string | null;
    /** Row heading (an h3), truncated to one line. */
    title: string;
    /** Glyph in the brand-tinted tile on the left. */
    icon: ComponentLike<{ Element: SVGElement }>;
    /** `standalone` (default) renders a raised card; `nested` renders a flat row inside another surface. */
    variant?: 'standalone' | 'nested';
  };
  Blocks: {
    /** Status pills next to the title. */
    badges?: [];
    /** Muted lines under the title. */
    meta?: [];
    /** Right-side content, e.g. amount pills. Hide it below `md` (`hidden md:flex`) or it overflows on phones. */
    metrics?: [];
  };
  Element: HTMLAnchorElement;
}

/** Linked list row: icon tile, title with badges, meta lines, and right-side metrics. */
export default class ListRowCard extends Component<ListRowCardSignature> {
  get containerClass() {
    if (this.args.variant === 'nested') {
      return 'tio-activity-card tio-activity-card--nested';
    }
    return 'tio-activity-card tio-card tio-card--radius-card tio-card--elevation-card tio-card--padding-sm tio-elevation-card-hover';
  }

  <template>
    <LinkTo @route={{@route}} @model={{@model}} class={{this.containerClass}} ...attributes>
      <div class="tio-row">
        <div class="tio-row-center h-12 w-12 shrink-0 rounded-lg tio-bg-brand-soft">
          {{#let @icon as |Icon|}}
            <Icon class="h-7 w-7 tio-fg-brand" />
          {{/let}}
        </div>
        <div class="tio-col-extra-tight tio-fill-min">
          <div class="tio-row-tight flex-wrap">
            <Heading @level={{3}} @size="base" class="truncate">{{@title}}</Heading>
            {{yield to="badges"}}
          </div>
          {{yield to="meta"}}
        </div>
        {{yield to="metrics"}}
      </div>
    </LinkTo>
  </template>
}
